import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LinkIcon, XMarkIcon } from '@heroicons/react/24/outline';
import apiService from '../../../services/ApiService';
import Modal from '../../ui/Modal';
import Button from '../../ui/Button';
import { useToast } from '../../../contexts/ToastContext';
import type {
  MediaModificationLink,
  MediaRelationshipType,
} from '../../../types/buildExpansion';
import type { GalleryImage } from './AlbumManagerModal';

/**
 * Owner tool to link gallery images to modifications (before/after,
 * installation, detail shots, etc.). Two-panel picker: choose an image and a
 * modification, pick the relationship, then link. Existing links can be
 * removed. Gallery images and modifications come from the parent.
 */

export interface ModificationOption {
  id: number | string;
  title: string;
}

const RELATIONSHIP_LABELS: Record<MediaRelationshipType, string> = {
  before: 'Before',
  after: 'After',
  installation: 'Installation',
  detail: 'Detail',
  event: 'Event',
  dyno: 'Dyno',
  performance: 'Performance',
  general: 'General',
};
const RELATIONSHIP_KEYS = Object.keys(RELATIONSHIP_LABELS) as MediaRelationshipType[];

export interface MediaLinkManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleId: number;
  /** The vehicle's gallery images, provided by the parent. */
  galleryImages: GalleryImage[];
  /** The vehicle's modifications, provided by the parent. */
  modifications: ModificationOption[];
  /** Called after any link or unlink so the parent can refresh. */
  onChanged?: () => void;
}

export const MediaLinkManagerModal: React.FC<MediaLinkManagerModalProps> = ({
  isOpen,
  onClose,
  vehicleId,
  galleryImages,
  modifications,
  onChanged,
}) => {
  const { showToast } = useToast();

  const [links, setLinks] = useState<MediaModificationLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);

  const [selectedImageId, setSelectedImageId] = useState<number | null>(null);
  const [selectedModId, setSelectedModId] = useState<string>('');
  const [relationshipType, setRelationshipType] = useState<MediaRelationshipType>('general');
  const [caption, setCaption] = useState('');
  const [formError, setFormError] = useState('');

  const imageById = useMemo(() => {
    const map = new Map<number, GalleryImage>();
    galleryImages.forEach((img) => map.set(img.id, img));
    return map;
  }, [galleryImages]);

  const loadLinks = useCallback(async () => {
    setLoadError('');
    try {
      const { media_links } = await apiService.getMediaAlbums(vehicleId);
      setLinks(media_links);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load existing links.');
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setSelectedImageId(null);
    setSelectedModId('');
    setRelationshipType('general');
    setCaption('');
    setFormError('');
    void loadLinks();
  }, [isOpen, loadLinks]);

  const handleLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedImageId == null) {
      setFormError('Choose an image to link.');
      return;
    }
    if (selectedModId === '') {
      setFormError('Choose a modification to link it to.');
      return;
    }
    setBusy(true);
    setFormError('');
    try {
      await apiService.mediaAlbumAction(vehicleId, 'link_modification', {
        image_id: selectedImageId,
        modification_id: Number(selectedModId),
        relationship_type: relationshipType,
        caption: caption.trim() || undefined,
      });
      void apiService.trackEvent('media_linked_to_modification', { vehicle_id: vehicleId });
      showToast('Image linked to modification.', 'success');
      setSelectedImageId(null);
      setSelectedModId('');
      setCaption('');
      await loadLinks();
      onChanged?.();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to link the image.');
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async (link: MediaModificationLink) => {
    setBusy(true);
    try {
      await apiService.mediaAlbumAction(vehicleId, 'unlink_modification', {
        image_id: link.image_id,
        modification_id: link.modification_id,
      });
      showToast('Link removed.', 'success');
      await loadLinks();
      onChanged?.();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to remove the link.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!busy) onClose();
      }}
      title="Link media to modifications"
      description="Connect gallery photos to the modifications they document."
      size="xl"
      closeOnOverlayClick={!busy}
      closeOnEscape={!busy}
    >
      <form onSubmit={handleLink} className="space-y-5">
        {formError && (
          <div
            className="p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm"
            role="alert"
          >
            {formError}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <fieldset>
            <legend className="block text-sm font-medium text-gray-300 mb-2">
              1. Choose an image
            </legend>
            {galleryImages.length === 0 ? (
              <p className="text-sm text-gray-400">
                No gallery images yet. Upload photos first, then link them here.
              </p>
            ) : (
              <ul
                className="grid grid-cols-3 sm:grid-cols-4 gap-2 list-none max-h-72 overflow-y-auto pr-1"
                aria-label="Gallery images"
              >
                {galleryImages.map((img) => {
                  const selected = selectedImageId === img.id;
                  return (
                    <li key={img.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedImageId(selected ? null : img.id);
                          if (formError) setFormError('');
                        }}
                        disabled={busy}
                        aria-pressed={selected}
                        aria-label={`${selected ? 'Deselect' : 'Select'} ${img.title || `image ${img.id}`}`}
                        className={`relative block w-full aspect-square rounded-lg overflow-hidden border-2 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${
                          selected ? 'border-orange-500' : 'border-white/[0.06] hover:border-white/[0.14]'
                        }`}
                      >
                        <img
                          src={img.image_url}
                          alt={img.title || ''}
                          className="w-full h-full object-cover"
                        />
                        {selected && (
                          <span
                            className="absolute inset-0 bg-orange-500/25 flex items-center justify-center"
                            aria-hidden
                          >
                            <span className="rounded-full bg-orange-500 text-white text-xs font-semibold px-2 py-0.5">
                              Selected
                            </span>
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </fieldset>

          <div className="space-y-4">
            <div>
              <label htmlFor="link-mod" className="block text-sm font-medium text-gray-300 mb-2">
                2. Choose a modification
              </label>
              {modifications.length === 0 ? (
                <p className="text-sm text-gray-400">
                  No modifications yet. Add modifications first, then link photos to them.
                </p>
              ) : (
                <select
                  id="link-mod"
                  value={selectedModId}
                  onChange={(e) => {
                    setSelectedModId(e.target.value);
                    if (formError) setFormError('');
                  }}
                  className="input min-h-[44px]"
                  disabled={busy}
                >
                  <option value="">Select a modification…</option>
                  {modifications.map((mod) => (
                    <option key={String(mod.id)} value={String(mod.id)}>
                      {mod.title}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label
                htmlFor="link-relationship"
                className="block text-sm font-medium text-gray-300 mb-1"
              >
                Relationship
              </label>
              <select
                id="link-relationship"
                value={relationshipType}
                onChange={(e) => setRelationshipType(e.target.value as MediaRelationshipType)}
                className="input min-h-[44px]"
                disabled={busy}
              >
                {RELATIONSHIP_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {RELATIONSHIP_LABELS[key]}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="link-caption" className="block text-sm font-medium text-gray-300 mb-1">
                Caption
              </label>
              <input
                type="text"
                id="link-caption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                maxLength={200}
                className="input min-h-[44px]"
                placeholder="Optional caption for this link"
                disabled={busy}
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              loading={busy}
              loadingLabel="Linking…"
              disabled={busy || galleryImages.length === 0 || modifications.length === 0}
              className="min-h-[44px]"
            >
              <LinkIcon className="w-4 h-4" aria-hidden />
              Link image
            </Button>
          </div>
        </div>

        <section aria-label="Existing links" className="pt-4 border-t border-white/[0.06]">
          <h4 className="text-meta font-medium mb-2">Existing links</h4>
          {loading ? (
            <div className="flex items-center gap-3 text-sm text-gray-400" role="status">
              <span className="spinner-sm" aria-hidden />
              Loading links…
            </div>
          ) : loadError ? (
            <div
              className="p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm"
              role="alert"
            >
              {loadError}{' '}
              <button
                type="button"
                onClick={() => {
                  setLoading(true);
                  void loadLinks();
                }}
                className="underline hover:text-white transition-colors cursor-pointer"
              >
                Try again
              </button>
            </div>
          ) : links.length === 0 ? (
            <p className="text-sm text-gray-400">No linked media yet.</p>
          ) : (
            <ul className="space-y-2 list-none">
              {links.map((link) => {
                const image = imageById.get(link.image_id);
                return (
                  <li
                    key={link.id}
                    className="rounded-lg border border-white/[0.06] bg-[#0c0c0c]/50 p-2.5 flex items-center gap-3"
                  >
                    {image ? (
                      <img
                        src={image.image_url}
                        alt={image.title || ''}
                        className="w-12 h-12 rounded object-cover bg-[#141414] shrink-0"
                      />
                    ) : (
                      <div
                        className="w-12 h-12 rounded bg-[#141414] shrink-0 flex items-center justify-center text-gray-600 text-[10px]"
                        aria-hidden
                      >
                        Image
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-200 truncate">{link.modification_title}</p>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {RELATIONSHIP_LABELS[link.relationship_type] ?? link.relationship_type}
                        {link.caption ? ` · ${link.caption}` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleUnlink(link)}
                      disabled={busy}
                      className="btn-icon min-w-[44px] min-h-[44px] text-red-300 hover:text-red-200 disabled:opacity-40 shrink-0 cursor-pointer"
                      aria-label={`Unlink image from ${link.modification_title}`}
                    >
                      <XMarkIcon className="w-4 h-4" aria-hidden />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </form>
    </Modal>
  );
};

export default MediaLinkManagerModal;
